import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { PanelNotice, PanelWaiting } from '@/components/hub/PanelNotice';
import {
  Abbinato,
  EntraInTutte,
  NotaTutte,
  PartitaTrovata,
  Rifiuto,
} from '@/components/hub/panels/CodeTorneiPanel';
import { type CodeTorneiValue } from '@/contexts/CodeTorneiContext';
import { useCodeTorneiPannello } from '@/hooks/useCodeTorneiPannello';
import { useHaptics } from '@/hooks/useHaptics';
import { useSyncProfiloLimulo } from '@/hooks/useSyncProfiloLimulo';
import { dbg } from '@/lib/logto';
import { degradedCopyKeys } from '@/lib/limulo/gating';
import {
  useCreateLimuloProfileMutation,
  useGetLimuloGameProfileRequirementsQuery,
  useLazyGetLimuloGameProfileRequirementsQuery,
  useLazyGetLimuloProfilesQuery,
  usePostLimuloProfileSyncMutation,
} from '@/services/limuloApi';
import {
  CODE_GAME_NAMES,
  CR_PROFILE_TYPE,
  QUEUE_EXIT_COPY,
  QUEUE_FORMAT_COPY,
  formatElapsed,
  queuesOfKind,
  tutteLeCodeSiOffrono,
  waitingInQueue,
  waitingSinceOf,
  type LimuloQueue,
  type LimuloQueueTicket,
} from '@/lib/limulo/code';
import { limuloFailure } from '@/lib/limulo/errors';
import { SlotPelleCode } from '@/lib/limulo/slotPelleCode';
import {
  attendiSyncProfilo,
  attesaProfili,
  creaEsincronizzaProfilo,
  motivoDelRifiuto,
  pianoIngressoCoda,
  profiloPerIscrizione,
} from '@/lib/limulo/registration';
import { LimuloDegraded } from '@/routes/limulo/LimuloDegraded';

/**
 * «Sit&Go» della Hub di Clash Royale — I SIT&GO di qTournaments (kind `sit-and-go`, ADR-0163), con
 * «Entra/Esci» come le code, al posto delle liste di QDuels e del monolite.
 *
 * 📜 Luca, L-2285 (2026-09-26 12:01:37Z), testuale: «(a) nel pannello «Sit&Go» della Hub CR, con
 * «Entra/Esci» come le code, al posto delle liste di QDuels e del monolite (solo su stg, a flag)».
 * Monta SOLO con `VITE_FEATURE_TOURNAMENT_SITNGO_ENABLED` in AND col flag delle code e Limulo (la
 * scelta sta in `SitngoPanel.tsx`, in un punto solo); a flag spento il pannello resta com'era.
 *
 * 🔴 Il meccanismo e' QUELLO delle code, non una copia: biglietto, rinnovo, ripresa e momento
 * vengono da `useCodeTorneiPannello` (lo stesso della «Ranked»), e «Abbinato», «Partita trovata» e
 * i rifiuti sono i componenti della Ranked. Il biglietto e' UNO per gioco: chi aspetta nella 1v1 e
 * in un sit&go lo vede in tutti e due i pannelli, e un abbinamento lo toglie da tutte le code.
 * Qui, a differenza della Ranked, l'elenco RESTA anche in attesa: si puo' stare in piu' sit&go
 * (ADR-0146, ADR-0162 §7), entrarci uno per uno e uscirne uno per uno (`leave(queueId)`).
 */
export function SitngoCodePanel({ profileType = CR_PROFILE_TYPE }: { profileType?: string } = {}) {
  const { t } = useTranslation('limulo');
  // Il nome che i testi dicono: `{{game}}`. Default Clash Royale, e il testo di prima resta identico.
  const game = CODE_GAME_NAMES[profileType] ?? profileType;
  const { code, gq, lq, profiliQ, crIds, lista, momento, inDiretta, nomi } = useCodeTorneiPannello(profileType);
  // 🔴 Sede 3 del sync del profilo (I-349): all'apertura del pannello, chi ha GIA' un profilo a zero campi per il
  // gioco delle code lo sincronizza (`useSyncProfiloLimulo`, fire-and-forget, una volta per profilo e per sessione):
  // i profili nati col «Collega» prima della cura non hanno `meta.provider.sub`, e la join prende
  // `queue.profile-rejected`. Clash Royale (schema con campi) non sincronizza mai. L'«Entra» aspetta comunque il
  // sync (`attendiSyncProfilo`): questo e' il caso in cui e' gia' partito prima del tap.
  useSyncProfiloLimulo(lista?.queues[0]?.gameId ?? '');

  if (gq.isLoading || (lq.isLoading && lq.data === undefined)) return <PanelWaiting slow={false} />;
  if (lq.error && lq.data === undefined) {
    return (
      <div className="flex flex-col py-2">
        <LimuloDegraded failure={limuloFailure(lq.error)} context="list" />
      </div>
    );
  }
  // Senza il catalogo non si sa quali code siano di Clash Royale: non e' «nessun sit&go».
  if (crIds === null || lista === null) return <PanelNotice tone="unavailable" />;

  const sitngo = queuesOfKind(lista.queues, 'sit-and-go');

  return (
    <div data-testid="sitngo-code-panel" className="flex h-full flex-col gap-3 overflow-y-auto pb-6 pt-3">
      {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. E anche il sottotitolo. */}
      <div className="sr-only">
        <span className="text-2xl font-extrabold leading-tight text-fg">
          {t('queues.sitngo.title', { defaultValue: 'Sit&Go' })}
        </span>
        <span className="text-sm font-semibold text-fg-muted">
          {t('queues.sitngo.subtitle', {
            game,
            defaultValue: '{{game}} · si parte appena il tavolo è pieno',
          })}
        </span>
      </div>

      {momento.kind === 'in-attesa' && code.ticket !== null && (
        <AttesaCompatta
          ticket={code.ticket}
          names={momento.queues.map((q) => nomi.get(q.queueId)?.name ?? q.queueId)}
          busy={code.busy}
          onLeaveAll={() => void code.leave()}
        />
      )}
      {momento.kind === 'abbinato' && <Abbinato />}
      {momento.kind === 'partita-trovata' && (
        <PartitaTrovata
          tournamentId={momento.tournamentId}
          auto={inDiretta}
          checkInMinutes={
            momento.queueId ? (nomi.get(momento.queueId)?.matchCheckInMinutes ?? null) : null
          }
        />
      )}
      {momento.kind === 'uscito' && (
        <p
          data-testid="sitngo-uscita"
          data-reason={momento.reason}
          className="rounded-xl bg-bg-secondary p-3 text-sm text-fg"
        >
          {t(QUEUE_EXIT_COPY[momento.reason])}
        </p>
      )}

      {code.refusal !== null && (
        <Rifiuto
          refusal={code.refusal}
          gameId={lista.queues[0]?.gameId ?? ''}
          ripiego={code.ticket?.lastMatch?.tournamentId ?? null}
        />
      )}
      {code.failure !== null && <LimuloDegraded failure={code.failure} context="list" />}

      {/* L'elenco da fuori E in attesa (si entra in un altro sit&go, se ne lascia uno), e a partita
          trovata sotto la sua carta (L-2436, la stessa regola della Ranked): via d'uscita se la TMA
          sbaglia, `queue.in-game` col link al torneo se e' giusta. Non da abbinati. */}
      {(momento.kind === 'fuori' ||
        momento.kind === 'uscito' ||
        momento.kind === 'in-attesa' ||
        momento.kind === 'partita-trovata') && (
        <ElencoSitngo
          game={game}
          gameId={lista.queues[0]?.gameId ?? ''}
          queues={sitngo}
          tutte={tutteLeCodeSiOffrono(lista.queues, code.ticket)}
          ticket={code.ticket}
          busy={code.busy}
          profili={profiliQ.data}
          profiliInErrore={profiliQ.isError}
          onJoin={(input) => void code.join(input)}
          onLeave={(queueId) => void code.leave(queueId)}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ElencoSitngo({
  game,
  gameId,
  queues,
  tutte,
  ticket,
  busy,
  profili,
  profiliInErrore,
  onJoin,
  onLeave,
}: {
  /** Il nome del gioco che il vuoto dice (`{{game}}`). */
  game: string;
  /** Il gioco di TUTTA la vetrina: le righe sono i soli sit&go, e possono essere zero col bottone acceso. */
  gameId: string;
  queues: LimuloQueue[];
  /** «Entra in tutte le code» si offre (`tutteLeCodeSiOffrono`): 1v1 e sit&go del gioco, la stessa regola della Ranked. */
  tutte: boolean;
  ticket: LimuloQueueTicket | null;
  busy: boolean;
  profili: Parameters<typeof profiloPerIscrizione>[0];
  profiliInErrore: boolean;
  onJoin: (input: Parameters<CodeTorneiValue['join']>[0]) => void;
  onLeave: (queueId: string) => void;
}) {
  const { t } = useTranslation('limulo');
  const haptics = useHaptics();
  const Pelle = useContext(SlotPelleCode);
  // Il profilo come nella Ranked e all'iscrizione (kb#237): uno ⇒ quello, piu' d'uno ⇒ il primo; e
  // la lista dei profili deve aver RISPOSTO, o un `queue.profile-required` sarebbe falso.
  const scelta = profiloPerIscrizione(profili, gameId);
  const stato = attesaProfili(scelta, profiliInErrore);
  const profileId =
    scelta.kind === 'uno'
      ? scelta.profileId
      : scelta.kind === 'piu'
        ? scelta.opzioni[0].profileId
        : null;
  /**
   * 🔴 **IL PROFILO PRIMA DELLA JOIN** (I-349, Luca sul telefono: «Entra» falliva a chi non aveva il profilo
   * backgammon, e con `queue.profile-rejected` a chi lo aveva). Il criterio e' lo SCHEMA del provider, mai il nome
   * del gioco (`pianoIngressoCoda`): a zero campi il profilo serve ESISTERE e AVERE il `sub` che solo il sync
   * scrive — la validazione di QGammon lo vuole. L'iscrizione ai tornei lo fa gia' (`iscriviConProfiloImplicito`);
   * qui e' lo STESSO passo (`creaEsincronizzaProfilo`), non una copia.
   * ⚠️ La GET parte solo a lista dei profili arrivata (`sconosciuto` ⇒ skip), ed e' la stessa voce di cache di
   * `useSyncProfiloLimulo`: una lettura sola per gioco.
   */
  const schemaQuery = useGetLimuloGameProfileRequirementsQuery(gameId, {
    skip: gameId === '' || scelta.kind === 'sconosciuto',
  });
  const piano = pianoIngressoCoda({
    scelta,
    gameId,
    requisitiGioco: schemaQuery.data,
    inErrore: schemaQuery.isError,
  });
  // Lo schema letto AL TAP (v. `entra`), solo per il caso `verifica-schema`.
  const [leggiSchema] = useLazyGetLimuloGameProfileRequirementsQuery();
  const [createProfile] = useCreateLimuloProfileMutation();
  const [syncProfile] = usePostLimuloProfileSyncMutation();
  // Lazy: la rilettura dei profili parte solo dopo un rifiuto della creazione (`createProfileReconciled`).
  const [readProfiles] = useLazyGetLimuloProfilesQuery();
  const [preparando, setPreparando] = useState(false);
  const [esito, setEsito] = useState<{ key: string; values?: Record<string, string> } | null>(null);
  // La guardia contro il doppio tap sta in una REF: `setPreparando(true)` non ha effetto sulla riga dopo, e
  // due tap nello stesso tick creerebbero due volte il profilo.
  const inCorso = useRef(false);

  const entra = async (target: { queueId: string } | { anyMode: true }) => {
    if (inCorso.current) return;
    inCorso.current = true;
    setPreparando(true);
    setEsito(null);
    haptics.impact('medium');
    try {
      let profilo = profileId;
      // Profilo presente e schema non ancora arrivato: «Entra» era acceso e senza righe (per Clash Royale sarebbe un
      // lampo a ogni prima apertura). Lo schema si aspetta ADESSO, agganciandosi alla lettura in volo
      // (`preferCacheValue`), e poi si decide: zero campi ⇒ sync atteso; con campi ⇒ join. Non letto ⇒ join come prima.
      let pianoOra = piano;
      if (piano === 'verifica-schema') {
        const letto = await leggiSchema(gameId, true);
        pianoOra = pianoIngressoCoda({
          scelta,
          gameId,
          requisitiGioco: letto.data,
          inErrore: letto.data === undefined,
        });
      }
      if (pianoOra === 'crea-profilo') {
        // Prima il profilo (col sync), poi la join col SUO id: quello di questo render e' `null` per costruzione
        // (la lista dice «nessuno»), e usarlo sarebbe la corsa di #375 a parti invertite.
        const creato = await creaEsincronizzaProfilo({
          createProfile: (b) => createProfile(b as { gameId: string } & Record<string, string>),
          readProfiles: () => readProfiles({ activeOnly: false }),
          syncProfile: (id) => syncProfile(id),
          log: dbg,
          gameId,
        });
        if (creato.fase === 'profilo-senza-id') {
          haptics.notification('warning');
          setEsito({ key: 'register.implicitProfile.noId' });
          return;
        }
        if (creato.fase === 'profilo') {
          // ⛔ Creazione fallita ⇒ NESSUNA join, e il motivo del provider invece del solo titolo del verdetto.
          haptics.notification('error');
          const rifiuto = creato.profilo.failure;
          setEsito({
            key:
              creato.profilo.outcome === 'refused'
                ? 'register.implicitProfile.refused'
                : 'register.implicitProfile.refusedUnknown',
            values: {
              reason: rifiuto
                ? motivoDelRifiuto(rifiuto, t(degradedCopyKeys(rifiuto.verdict).titleKey))
                : t('degraded.unknown.title'),
            },
          });
          return;
        }
        profilo = creato.profileId;
      } else if (pianoOra === 'sincronizza' && profilo !== null) {
        // Il profilo c'e': il sync si ASPETTA (agganciandosi a quello in volo, se la scheda o il «Collega» l'ha
        // gia' lanciato). Un tap veloce non deve battere il sync. Non blocca: un rifiuto va nel registro, e se il
        // `sub` manca davvero lo dice la join col suo motivo.
        await attendiSyncProfilo({ profileId: profilo, sync: (id) => syncProfile(id), log: dbg });
      }
      onJoin({ gameId, ...target, profileId: profilo });
    } finally {
      setPreparando(false);
      inCorso.current = false;
    }
  };

  // Il vuoto: da solo (nessun modello e nessun bottone) o sotto il bottone «tutte» (il gioco ha altri modelli, ma non sit&go).
  const vuoto = (
    <p
      data-testid="sitngo-vuoti"
      className="rounded-xl bg-bg-secondary p-4 text-center text-sm text-fg-muted"
    >
      {t('queues.sitngo.empty', {
        game,
        defaultValue: 'Nessun sit&go aperto per {{game}} in questo momento.',
      })}
    </p>
  );
  if (queues.length === 0 && !tutte) return vuoto;
  const nota =
    stato === 'in-lettura'
      ? t('queues.ui.readingProfiles', { defaultValue: 'Leggo i tuoi profili di gioco…' })
      : stato === 'illeggibili'
        ? t('queues.ui.profilesUnreadable', {
            defaultValue: 'Non riesco a leggere i tuoi profili di gioco: riprova tra poco.',
          })
        : null;
  const attesaSchema = piano === 'attendi' || piano === 'illeggibile';

  const entraSpento = busy || stato !== 'pronti' || preparando || attesaSchema;

  if (Pelle?.ElencoSitngo)
    return (
      <Pelle.ElencoSitngo
        vista={{
          queues,
          ticket,
          tutte,
          busy,
          preparando,
          entraSpento,
          nota,
          attesaSchema,
          piano,
          stato,
          esito,
          vuoto,
          game,
          entra: (target) => void entra(target),
          riprovaSchema: () => void schemaQuery.refetch(),
          onLeave,
        }}
      />
    );

  return (
    <div className="flex flex-col gap-2">
      {tutte && (
        <EntraInTutte
          testId="sitngo-tutte"
          busy={busy || preparando}
          disabled={entraSpento}
          onClick={() => void entra({ anyMode: true })}
        />
      )}
      {nota !== null ? (
        <p className="text-center text-xs text-fg-muted">{nota}</p>
      ) : tutte ? (
        <NotaTutte game={game} />
      ) : null}
      {attesaSchema && stato === 'pronti' && (
        <div
          data-testid="sitngo-schema-attesa"
          className="flex items-center justify-between gap-2 text-xs leading-relaxed text-fg-muted"
        >
          <p>{piano === 'illeggibile' ? t('register.schema.unreadable') : t('register.schema.reading')}</p>
          {piano === 'illeggibile' && (
            <button
              type="button"
              data-testid="sitngo-schema-riprova"
              onClick={() => void schemaQuery.refetch()}
              className="shrink-0 rounded-lg px-2 py-1 font-bold text-brand active:opacity-80"
            >
              {t('register.profiles.retry')}
            </button>
          )}
        </div>
      )}
      {/* I due esiti del profilo creato da «Entra»: la frase che spiega, una volta, che il profilo lo crea l'app;
          e il rifiuto, col suo motivo. Sono le frasi dell'iscrizione: nessun testo nuovo. */}
      {piano === 'crea-profilo' && esito === null && (
        <p data-testid="sitngo-profilo-implicito" className="text-center text-xs text-fg-muted">
          {t('register.implicitProfile.notice')}
        </p>
      )}
      {esito !== null && (
        <p data-testid="sitngo-profilo-esito" className="rounded-xl bg-danger/10 p-3 text-sm text-fg">
          {t(esito.key, esito.values)}
        </p>
      )}
      {queues.length === 0 ? (
        vuoto
      ) : (
        <ul className="flex flex-col gap-2">
          {queues.map((q) => (
            <li key={q.queueId}>
              <RigaSitngo
                queue={q}
                inCoda={waitingInQueue(ticket, q.queueId)}
                entraSpento={entraSpento}
                esciSpento={busy}
                onJoin={() => void entra({ queueId: q.queueId })}
                onLeave={() => onLeave(q.queueId)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RigaSitngo({
  queue,
  inCoda,
  entraSpento,
  esciSpento,
  onJoin,
  onLeave,
}: {
  queue: LimuloQueue;
  inCoda: boolean;
  entraSpento: boolean;
  esciSpento: boolean;
  onJoin: () => void;
  onLeave: () => void;
}) {
  const { t } = useTranslation('limulo');
  const formato =
    queue.format && QUEUE_FORMAT_COPY[queue.format] ? t(QUEUE_FORMAT_COPY[queue.format]) : null;
  const partiti =
    queue.groupsStarted !== null
      ? t('queues.sitngo.started', { count: queue.groupsStarted, defaultValue: '{{count}} partiti' })
      : null;
  return (
    <div
      data-testid="sitngo-riga"
      data-queue-id={queue.queueId}
      data-in-coda={inCoda ? 'si' : 'no'}
      className={`card-edge flex items-center gap-3 rounded-xl border px-4 py-3 ${inCoda ? 'border-brand bg-brand/10' : 'border-transparent bg-bg-secondary'}`}
    >
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-bold text-fg">{queue.name}</span>
          {/* La riga in cui si aspetta si riconosce anche senza leggere il bottone (revisione di #444, 🟡). */}
          {inCoda && (
            <span
              data-testid="sitngo-qui-in-attesa"
              className="shrink-0 rounded-full bg-brand/20 px-2 py-0.5 text-[11px] font-bold text-brand"
            >
              {t('queues.sitngo.waitingHere', { defaultValue: 'In attesa' })}
            </span>
          )}
        </span>
        <span className="truncate text-xs font-semibold text-fg-muted">
          {[
            t('queues.ui.kindSitAndGo', { size: queue.size, defaultValue: 'Sit&go da {{size}}' }),
            formato,
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        {partiti !== null && <span className="truncate text-xs text-fg-muted">{partiti}</span>}
      </div>
      {inCoda ? (
        <button
          type="button"
          data-testid="sitngo-esci"
          disabled={esciSpento}
          onClick={onLeave}
          className="shrink-0 rounded-lg bg-bg-tertiary px-3 py-2 text-sm font-bold text-fg-muted active:opacity-80 disabled:opacity-40"
        >
          {t('queues.sitngo.leave', { defaultValue: 'Esci' })}
        </button>
      ) : (
        <button
          type="button"
          data-testid="sitngo-entra"
          disabled={entraSpento}
          onClick={onJoin}
          className="shrink-0 rounded-lg bg-brand/15 px-3 py-2 text-sm font-bold text-brand active:opacity-80 disabled:opacity-40"
        >
          {t('queues.ui.join', { defaultValue: 'Entra' })}
        </button>
      )}
    </div>
  );
}

/**
 * «Sei in coda» in piccolo, sopra l'elenco: le code del biglietto (1v1 e sit&go, per nome), da
 * quanto, l'avviso sull'app chiusa, e «Esci da tutte». Le stesse regole di `InAttesa` della Ranked:
 * il tempo si mostra solo se c'e' un istante leggibile, e l'orologio e' SOLO testo.
 */
function AttesaCompatta({
  ticket,
  names,
  busy,
  onLeaveAll,
}: {
  ticket: LimuloQueueTicket;
  names: string[];
  busy: boolean;
  onLeaveAll: () => void;
}) {
  const { t } = useTranslation('limulo');
  const Pelle = useContext(SlotPelleCode);
  const da = waitingSinceOf(ticket.queues);
  const [adesso, setAdesso] = useState(() => Date.now());
  useEffect(() => {
    if (da === null) return;
    const h = window.setInterval(() => setAdesso(Date.now()), 1000);
    return () => window.clearInterval(h);
  }, [da]);
  const secondi =
    ticket.expiresAfterMs !== null && ticket.expiresAfterMs > 0
      ? Math.round(ticket.expiresAfterMs / 1000)
      : null;
  if (Pelle?.InAttesa)
    return <Pelle.InAttesa vista={{ nomi: names, da, adesso, secondi, busy, onLeave: onLeaveAll }} />;
  return (
    <div data-testid="sitngo-in-attesa" className="flex flex-col gap-2 rounded-xl bg-brand/10 px-4 py-3">
      <div className="flex items-center gap-3">
        <Loader2 className="h-5 w-5 shrink-0 animate-spin text-brand" />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-sm font-bold text-fg">
            {t('queues.ui.inQueue', { defaultValue: 'Sei in coda' })}
          </span>
          <span className="truncate text-xs text-fg-muted">
            {t('queues.ui.inQueueFor', { names: names.join(', '), defaultValue: 'Code: {{names}}' })}
          </span>
        </div>
        {da !== null && (
          <span data-testid="sitngo-tempo" className="shrink-0 text-lg font-extrabold tabular-nums text-fg">
            {formatElapsed(adesso - da)}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3">
        <p className="min-w-0 flex-1 text-xs text-fg-muted">
          {secondi !== null
            ? t('queues.ui.keepOpen', {
                seconds: secondi,
                defaultValue:
                  "Tieni l'app aperta: se resta chiusa più di {{seconds}} secondi esci dalla coda.",
              })
            : t('queues.ui.keepOpenGeneric', {
                defaultValue: "Tieni l'app aperta: se resta chiusa troppo a lungo esci dalla coda.",
              })}
        </p>
        <button
          type="button"
          data-testid="sitngo-esci-tutte"
          disabled={busy}
          onClick={onLeaveAll}
          className="shrink-0 rounded-lg bg-bg-tertiary px-3 py-2 text-xs font-bold text-fg-muted active:opacity-80 disabled:opacity-50"
        >
          {/* Come la Ranked: `leave()` esce da tutto il biglietto, e lo si dice solo quando «tutto» e'
              piu' di una coda (revisione di #444, 🟡). */}
          {names.length > 1
            ? t('queues.ui.leaveAll', { defaultValue: 'Esci da tutte' })
            : t('queues.ui.leave', { defaultValue: 'Esci dalla coda' })}
        </button>
      </div>
    </div>
  );
}
